import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, Table, Tag, Tooltip, message,
} from 'antd';
import { ReloadOutlined, SyncOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  listCustomerDirectoryReindexJobs,
  startCustomerDirectoryDeltaReindex,
  startCustomerDirectoryFullReindex,
} from '../../services/customerImportApi';
import { STATUS_META } from './CustomerImportProgressPanel';

const ACTIVE_JOB_STATUSES = ['queued', 'running'];
const ACTIVE_POLL_INTERVAL_MS = 3000;

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
};

const MODE_LABELS = {
  full: 'Full',
  delta: 'Delta',
};

// Summary shape varies by mode/outcome -- see reindex_service.py's
// full_reindex_account / delta_reindex_account / smart_reindex_after_import
// return shapes. Render whatever counts are present rather than assuming one
// fixed shape.
const summarizeResult = (summary) => {
  if (!summary) return '—';
  const parts = [];
  if (typeof summary.customers_indexed === 'number') parts.push(`${summary.customers_indexed} customers`);
  if (typeof summary.shipto_indexed === 'number') parts.push(`${summary.shipto_indexed} ship-to`);
  if (!parts.length) return '—';
  const prefix = summary.mode === 'full_fallback' ? 'full (no prior reindex)' : (MODE_LABELS[summary.mode] || summary.mode);
  return `${prefix}: ${parts.join(', ')}`;
};

// Manual full/delta reindex of the customer_directory OpenSearch collection,
// on top of the automatic indexing that already happens at import time
// (CustomerImportJobsPanel / SyncProfilesPanel). Full reindex sweeps every
// active customer + ship-to address for the account (batched/bulk, not one
// HTTP call per row -- see customer_directory_service.index_customers_for_account);
// delta only reindexes rows changed since the last successful full/delta run.
export default function ReindexPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startingMode, setStartingMode] = useState(null);
  const pollRef = useRef(null);

  const fetchJobs = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const result = await listCustomerDirectoryReindexJobs({ limit: 10 });
      setJobs(result?.jobs || []);
    } catch (err) {
      if (!background) message.error(err.message || 'Could not load reindex jobs.');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const hasActiveJob = jobs.some((j) => ACTIVE_JOB_STATUSES.includes(j.status));

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!hasActiveJob) return undefined;
    pollRef.current = setInterval(() => fetchJobs({ background: true }), ACTIVE_POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasActiveJob, fetchJobs]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const handleStart = async (mode) => {
    setStartingMode(mode);
    try {
      const result = mode === 'full'
        ? await startCustomerDirectoryFullReindex()
        : await startCustomerDirectoryDeltaReindex();
      message.success(
        result.resumed
          ? 'A reindex was already running — showing its progress.'
          : `${mode === 'full' ? 'Full' : 'Delta'} reindex started.`,
      );
      await fetchJobs();
    } catch (err) {
      message.error(err.message || 'Could not start reindex.');
    } finally {
      setStartingMode(null);
    }
  };

  const columns = [
    {
      title: 'Mode',
      dataIndex: 'mode',
      width: 90,
      render: (mode) => <Tag>{MODE_LABELS[mode] || mode}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        const meta = STATUS_META[status] || STATUS_META.queued;
        return <Tag color={meta.color} icon={meta.icon}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Result',
      key: 'summary',
      render: (_, job) => summarizeResult(job.summary),
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      render: formatDate,
    },
    {
      title: 'Finished',
      dataIndex: 'finished_at',
      render: formatDate,
    },
    {
      title: 'Error',
      dataIndex: 'error',
      render: (value) => (value ? (
        <Tooltip title={value}>
          <span className="text-danger small text-truncate d-inline-block" style={{ maxWidth: 220 }}>{value}</span>
        </Tooltip>
      ) : '—'),
    },
  ];

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button
          icon={<ThunderboltOutlined />}
          loading={startingMode === 'delta'}
          disabled={hasActiveJob}
          onClick={() => handleStart('delta')}
        >
          Delta Reindex
        </Button>
        <Button
          icon={<SyncOutlined />}
          loading={startingMode === 'full'}
          disabled={hasActiveJob}
          onClick={() => handleStart('full')}
        >
          Full Reindex
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => fetchJobs()} loading={loading} />
        <span className="small text-muted ms-2">
          Delta reindexes only rows changed since the last run; Full sweeps every active
          customer and ship-to address. Both run in the background — safe to navigate away.
        </span>
      </div>

      <Table
        rowKey="job_id"
        size="small"
        loading={loading}
        dataSource={jobs}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'No reindex jobs yet.' }}
      />
    </div>
  );
}
